import {
  createStyles,
  Grid,
  makeStyles,
  Paper,
  Theme,
} from "@material-ui/core";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FC } from "react";
import { useAuth } from "auth";
import { RouteParams } from "Models/RouteParams";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import {
  GET_BLOCK_WITH_MARKERS_BY_ID,
  GetBlockWithMarkersQueryResult,
} from "Graphql/BlockQueries";
import moment from "moment";
import { CPODailyProductionData } from "Models/CPODailyProductionData";
import Select, { MultiValue } from "react-select";
import { OptionType } from "Models/OptionType";
import { TableIcons } from "../Icons/TableIcons";
import { CustomStylesSubSystemOptions } from "Logic/SubSystemOptions/CustomStylesSubSystemOptions";
import { KeycloakProfile } from "auth";
import { CPOEditReasons } from "Models/CPOEditReasons";
import { CPODeferralAssets } from "Models/CPODeferralAssets";
import { CPOFacilities } from "Models/CPOFacilities";
import { CheckboxOption } from "Logic/SubSystemOptions/CheckboxOption";
import { Roles } from "Enums/Roles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      display: "flex",
      alignItems: "stretch",
    },
    dropdown: {
      margin: theme.spacing(2),
      minWidth: 100,
    },

    monthPicker: {
      padding: theme.spacing(1),
    },
    monthPickerGrid: {
      paddingBottom: theme.spacing(1),
      flexWrap: "nowrap",
    },
  }),
);

export const CPODailyProductionValuesTable: FC = () => {
  const classes = useStyles();
  const { blockId } = useParams<RouteParams>();
  const { t } = useTranslation("cpo");
  const { auth } = useAuth();
  const [userProfile, setUserProfile] = useState<KeycloakProfile | undefined>(
    undefined,
  );

  const [cpoMonthProductionData, setCpoMonthProductionData] = useState<
    CPODailyProductionData[] | undefined
  >(undefined);
  const [cpoDeferralAssets, setCpoDeferralAssets] = useState<
    CPODeferralAssets[] | undefined
  >(undefined);
  const [selectedAssets, setSelectedAssets] = useState<MultiValue<OptionType>>(
    [],
  );
  // const [datePickerChangedBool, setDatePickerChangedBool] = useState(false);

  const [showFacilitiesBool, setShowFacilitiesBool] = useState(false);
  const [cpoFacilities, setCpoFacilities] = useState<
    CPOFacilities[] | undefined
  >(undefined);
  const [selectedFacilities, setSelectedFacilities] = useState<
    MultiValue<OptionType>
  >([]);

  const [selectedMonth, setSelectedMonth] = useState<string>(
    moment().format("MMMM"),
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    moment().format("YYYY"),
  );
  const [cpoEditReasons, setCpoEditReasons] = useState<
    CPOEditReasons[] | undefined
  >(undefined);

  const { data: blockData } = useQuery<GetBlockWithMarkersQueryResult>(
    GET_BLOCK_WITH_MARKERS_BY_ID,
    { variables: { id: blockId } },
  );

  const monthOptions = useMemo(() => {
    return moment.months().map((month) => ({
      value: month,
      label: month,
    }));
  }, []);

  const yearOptions = useMemo(() => {
    const currentYear = moment().year();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    return years.map((year) => ({
      value: year.toString(),
      label: year.toString(),
    }));
  }, []);

  const fetchCpoDailyProductionData = useCallback(
    async (
      month: number,
      year: number,
      blockData: GetBlockWithMarkersQueryResult,
    ) => {
      if (blockData.block.name === "0") {
        setShowFacilitiesBool(true);
      } else {
        setShowFacilitiesBool(false);
      }

      const blockQueryText = blockData?.block.name
        .replace(" ", "_")
        .toLowerCase();
      const url =
        process.env.REACT_APP_CPO_API_URL +
        `/api/daily/production?month=${month}&year=${year}&block=${blockQueryText}`;
      try {
        const token = await auth?.getToken();
        const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    }
                  });
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          return;
        }

        const rawText = await response.text();

        const validJsonText = rawText.replace(/: NaN/g, ": null");

        const responseJsonParsed = JSON.parse(validJsonText);
        const rawData = JSON.parse(responseJsonParsed);
        console.log(rawData.data);

        setCpoMonthProductionData(rawData.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    if (selectedMonth && selectedYear && blockData) {
      const monthNumber = moment().month(selectedMonth).month() + 1;
      fetchCpoDailyProductionData(
        monthNumber,
        parseInt(selectedYear, 10),
        blockData,
      );
    }
  }, [selectedMonth, selectedYear, fetchCpoDailyProductionData, blockData]);

  const filteredTableData = useMemo(() => {
    const dataToFilter = cpoMonthProductionData || [];
    console.log(dataToFilter);
    const selectedAssetNames = selectedAssets.map((asset) => asset.value);
    const selectedFacilityIds = selectedFacilities.map((facility) =>
      parseInt(facility.value, 10),
    );

    return dataToFilter.filter((row) => {
      const passesAssetFilter =
        selectedAssetNames.length === 0 ||
        selectedAssetNames.includes(row.asset);

      const passesFacilityFilter =
        selectedFacilityIds.length === 0 ||
        selectedFacilityIds.includes(row.Facility_Ref);

      return passesAssetFilter && passesFacilityFilter;
    });
  }, [cpoMonthProductionData, selectedAssets, selectedFacilities]);

  const Option = CheckboxOption;

  useEffect(() => {
    async function fetchCpoEditReasons() {
      if (blockData) {
        const url = process.env.REACT_APP_CPO_API_URL + `/api/edit/reason`;
        try {
          const token = await auth?.getToken();
          const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    }
                  });
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
          }

          const rawJsonString = await response.json();
          const responseData = JSON.parse(rawJsonString);

          if (
            responseData &&
            typeof responseData === "object" &&
            responseData !== null &&
            Array.isArray(responseData.editReasons)
          ) {
            const category = "reasonCategory";
            const categoryValue = ["production", "general"];

            const filteredEditReasons = responseData.editReasons.filter(
              (reason) => categoryValue.includes(reason[category]),
            );
            setCpoEditReasons(filteredEditReasons);
          } else {
            console.warn(
              "WARNING: API response did not contain an array 'edit reasons' as expected.",
              responseData,
            );
          }
        } catch (error) {
          console.error("Error fetching edit reasons:", error);
        }
      } else {
        console.error(
          "fetchCpoEditReasons skipped because blockData is falsy.",
        );
      }
    }
    fetchCpoEditReasons();
    // eslint-disable-next-line
  }, [blockData]);

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await auth?.loadUserProfile();
      setUserProfile(response);
    } catch (error) {
      auth?.logout();
    }
  }, [auth]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    async function fetchCpoAssets() {
      if (blockData) {
        const blockQueryText = blockData?.block.name
          .replace(" ", "_")
          .toLowerCase();
        const url =
          process.env.REACT_APP_CPO_API_URL +
          `/api/assets?block=${blockQueryText}`;
        try {
          const token = await auth?.getToken();
          const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    }
                  });
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
          }

          const rawJsonString = await response.json();
          const responseData = JSON.parse(rawJsonString);

          if (
            responseData &&
            typeof responseData === "object" &&
            responseData !== null &&
            Array.isArray(responseData.cpoAssets)
          ) {
            setCpoDeferralAssets(responseData.cpoAssets);
          } else {
            console.warn(
              "WARNING: API response did not contain an array 'cpoAssets' as expected.",
              responseData.cpoAssets,
            );
          }
        } catch (error) {
          console.error("Error fetching assets:", error);
        }
      } else {
        console.error("fetchCpoAssets skipped because blockData is falsy.");
      }
    }
    fetchCpoAssets();
    // eslint-disable-next-line
  }, [blockData]);

  const assetNameLookup = useMemo(() => {
    if (!cpoDeferralAssets || !Array.isArray(cpoDeferralAssets)) {
      return [];
    }
    const options: OptionType[] = cpoDeferralAssets.map((asset) => {
      return {
        value: asset.assetName,
        label: asset.assetName,
      };
    });
    return options;
  }, [cpoDeferralAssets]);

  const handleAssetChange = (option: MultiValue<OptionType>) => {
    setSelectedAssets(option);
  };

  useEffect(() => {
    async function fetchCpoFacilities() {
      if (blockData) {
        const blockQueryText = blockData?.block.name
          .replace(" ", "_")
          .toLowerCase();
        const url =
          process.env.REACT_APP_CPO_API_URL +
          `/api/facilities?block=${blockQueryText}`;
        try {
          const token = await auth?.getToken();
          const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    }
                  });
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
          }

          const rawJsonString = await response.json();
          const responseData = JSON.parse(rawJsonString);

          console.log(responseData);

          if (
            responseData &&
            typeof responseData === "object" &&
            responseData !== null &&
            Array.isArray(responseData.cpoFacilities)
          ) {
            setCpoFacilities(responseData.cpoFacilities);
          } else {
            console.warn(
              "WARNING: API response did not contain an array 'cpoFacilities' as expected.",
              responseData.cpoFacilities,
            );
          }
        } catch (error) {
          console.error("Error fetching assets:", error);
        }
      } else {
        console.error("fetchCpoAssets skipped because blockData is falsy.");
      }
    }
    fetchCpoFacilities();
    // eslint-disable-next-line
  }, [blockData]);

  const facilitiesNameLookup = useMemo(() => {
    if (!cpoFacilities || !Array.isArray(cpoFacilities)) {
      return [];
    }
    const options: OptionType[] = cpoFacilities.map((facility) => {
      return {
        value: facility.facilityId.toString(),
        label: facility.facilityName,
      };
    });
    console.log(options);

    return options;
  }, [cpoFacilities]);

  const handleFacilityChange = (option: MultiValue<OptionType>) => {
    setSelectedFacilities(option);
  };

  if (
    !auth?.hasRealmRole(Roles.cpoDailyProductionValuesEditor) &&
    !auth?.hasRealmRole(Roles.administrator)
  ) {
    return null;
  }

  return (
    <>
      <Grid container className={classes.monthPickerGrid}>
          <Grid item xs={2} className={classes.dropdown}>
            <Select
              onChange={(option) => {
                // setDatePickerChangedBool(true);
                setSelectedMonth((option as OptionType).value);
              }}
              options={monthOptions}
              value={monthOptions.find(
                (option) => option.value === selectedMonth,
              )}
              isMulti={false}
              styles={CustomStylesSubSystemOptions}
              placeholder={t("selectMonth")}
            />
          </Grid>
          <Grid item xs={2} className={classes.dropdown}>
            <Select
              onChange={(option) => {
                // setDatePickerChangedBool(true);
                setSelectedYear((option as OptionType).value);
              }}
              options={yearOptions}
              value={yearOptions.find(
                (option) => option.value === selectedYear,
              )}
              isMulti={false}
              styles={CustomStylesSubSystemOptions}
              placeholder={t("selectYear")}
            />
          </Grid>
          <Grid item xs={2} className={classes.dropdown}>
            <Select
              onChange={(option) => handleAssetChange(option)}
              options={assetNameLookup}
              isMulti
              autoFocus
              isSearchable
              isClearable
              styles={CustomStylesSubSystemOptions}
              placeholder={t("filterAssets")}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{
                Option,
              }}
            />
          </Grid>
          {showFacilitiesBool && (
            <Grid item xs={2} className={classes.dropdown}>
              <Select
                onChange={(option) => handleFacilityChange(option)}
                options={facilitiesNameLookup}
                isMulti
                autoFocus
                isSearchable
                isClearable
                styles={CustomStylesSubSystemOptions}
                placeholder={t("filterFacilities")}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{
                  Option,
                }}
              />
            </Grid>
          )}
        </Grid>

        <Paper className={classes.root}>
          <Grid item xs={12}>
            <MaterialTable
              title={
                t("dailyProdValuesTitle") + blockData?.block.name
              }
              localization={{
                toolbar: {
                  searchPlaceholder: t("searchPlaceholder"),
                },
                body: {
                  bulkEditTooltip: t("editProdValuesTooltip"),
                  emptyDataSourceMessage: t("noProdValuesRecordsMessage"),
                } as any
                //May need to add other translations for the tables here
              }}
              options={{
                actionsCellStyle: {
                  width: "0%",
                },
                actionsColumnIndex: -1,
                draggable: false,
                selection: false,
                padding: "default",
                pageSizeOptions: [],
                showFirstLastPageButtons: false,
                pageSize: 15,
                tableLayout: "auto",
                detailPanelType: "single",
                search: true,
                filtering: false,
              }}
              data={filteredTableData}
              icons={TableIcons}
              editable={{
                onBulkUpdate: (changes) =>
                  new Promise<void>(async (resolve, reject) => {
                    const changesArray = Object.values(changes);

                    const newDataArray = changesArray.map(
                      (change) => change.newData,
                    );

                    let latestEditor =
                      userProfile?.firstName + " " + userProfile?.lastName;

                    const updatedDataForApi = newDataArray.map((item) => ({
                      ...item,
                      latestEditor: latestEditor,
                      editReason: String(
                        item.editReason === undefined ? 1 : item.editReason,
                      ),
                      Facility_Ref: String(item.Facility_Ref),
                    }));

                    try {
                      const url =
                        process.env.REACT_APP_CPO_API_URL +
                        `/api/edit/daily/production`;
                      const token = await auth?.getToken();
                      const response = await fetch(url, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(updatedDataForApi),
                      });

                      if (!response.ok) {
                        const errorText = await response.text();
                        console.error("Server error:", errorText);
                        reject(
                          new Error(
                            `Failed to update data: ${response.statusText}`,
                          ),
                        );
                        return;
                      }

                      const result = await response.json();
                      console.log("Bulk update successful:", result);

                      if (selectedMonth && selectedYear && blockData) {
                        const monthNumber =
                          moment().month(selectedMonth).month() + 1;
                        fetchCpoDailyProductionData(
                          monthNumber,
                          parseInt(selectedYear, 10),
                          blockData,
                        );
                      }

                      resolve();
                    } catch (error) {
                      console.error("Error sending bulk update:", error);
                      reject(error);
                    }
                  }),
              }}
              columns={[
                {
                  title: t("dateProdValuesTable"),
                  field: "date",
                  editable: "never",
                  type: "date",
                  initialEditValue: "",
                  filtering: false,
                  cellStyle: {
                    width: "10%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "10%",
                  },
                  // defaultFilter:
                  //   datePickerChangedBool === false && defaultFilterDate
                  //     ? defaultFilterDate
                  //     : null,
                  render: (rowData) => {
                    const date = new Date(rowData.date);
                    if (isNaN(date.getTime())) {
                      return "";
                    }
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");

                    return `${year}-${month}-${day}`;
                  },
                },
                {
                  title: t("assetProdValuesTable"),
                  field: "asset",
                  editable: "never",
                  filtering: false,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                },

                {
                  title: t("facilityProdValuesTable"),
                  field: "facility",
                  editable: "never",
                  filtering: false,
                  hidden: !showFacilitiesBool,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const matchedFacility = cpoFacilities?.find(
                      (facility) =>
                        rowData.Facility_Ref === facility.facilityId,
                    );
                    if (matchedFacility) {
                      return matchedFacility.facilityName;
                    } else {
                      return "N/A";
                    }
                  },
                  lookup: cpoFacilities?.reduce((acc, facility) => {
                    acc[facility.facilityId] = facility.facilityName;
                    return acc;
                  }, {}),
                },
                {
                  title: "Gas Export (MMSCFD)",
                  field: "Gas_Exp_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Gas Flare (MMSCFD)",
                  field: "Gas_Flare_MMSCFD",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Fuel Gas (MMSCFD)",
                  field: "Gas_Fuel_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Gas Import (MMSCFD)",
                  field: "Gas_Imp_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Gas Injection (MMSCFD)",
                  field: "Gas_Inj_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Technical Production Gas (MMSCFD)",
                  field: "Gas_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Production Gas (MMSCFD) - IPC",
                  field: "Gas_MMSCFD_IPC",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "RB Gas Lift (MMSCFD)",
                  field: "Gas_RBGL_MMSCFD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: t("oilProdValuesTable"),
                  field: "Oil_BOPD_Actual",
                  filtering: false,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const oilBOPDActualValue = rowData.Oil_BOPD_Actual;
                    if (
                      typeof oilBOPDActualValue === "number" &&
                      !isNaN(oilBOPDActualValue)
                    ) {
                      return oilBOPDActualValue.toFixed(3);
                    }
                    if (
                      typeof oilBOPDActualValue === "string" &&
                      !isNaN(parseFloat(oilBOPDActualValue))
                    ) {
                      return parseFloat(oilBOPDActualValue).toFixed(3);
                    }
                    return oilBOPDActualValue ?? "";
                  },
                },
                {
                  title: t("oilIpcProdValuesTable"),
                  field: "Oil_BOPD_IPC",
                  filtering: false,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const oilBOPDIPCValue = rowData.Oil_BOPD_IPC;
                    if (
                      typeof oilBOPDIPCValue === "number" &&
                      !isNaN(oilBOPDIPCValue)
                    ) {
                      return oilBOPDIPCValue.toFixed(3);
                    }
                    if (
                      typeof oilBOPDIPCValue === "string" &&
                      !isNaN(parseFloat(oilBOPDIPCValue))
                    ) {
                      return parseFloat(oilBOPDIPCValue).toFixed(3);
                    }
                    return oilBOPDIPCValue ?? "";
                  },
                },
                {
                  title: t("oilExportProdValuesTable"),
                  field: "Oil_Exp_BOPD_Actual",
                  filtering: false,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const oilExpBOPDActualValue = rowData.Oil_Exp_BOPD_Actual;
                    if (
                      typeof oilExpBOPDActualValue === "number" &&
                      !isNaN(oilExpBOPDActualValue)
                    ) {
                      return oilExpBOPDActualValue.toFixed(3);
                    }
                    if (
                      typeof oilExpBOPDActualValue === "string" &&
                      !isNaN(parseFloat(oilExpBOPDActualValue))
                    ) {
                      return parseFloat(oilExpBOPDActualValue).toFixed(3);
                    }
                    return oilExpBOPDActualValue ?? "";
                  },
                },
                {
                  title: t("oilBarrelsInTankProdValuesTable"),
                  field: "Oil_Stock_BBL",
                  filtering: false,
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const oilStockBBLValue = rowData.Oil_Stock_BBL;
                    if (
                      typeof oilStockBBLValue === "number" &&
                      !isNaN(oilStockBBLValue)
                    ) {
                      return oilStockBBLValue.toFixed(3);
                    }
                    if (
                      typeof oilStockBBLValue === "string" &&
                      !isNaN(parseFloat(oilStockBBLValue))
                    ) {
                      return parseFloat(oilStockBBLValue).toFixed(3);
                    }
                    return oilStockBBLValue ?? "";
                  },
                },
                {
                  title: "Water (BWPD)",
                  field: "Water_BWPD_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Water (BWPD) - IPC",
                  field: "Water_BWPD_IPC",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Water Injection",
                  field: "Water_Inj_Actual",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Water Injection - IIC",
                  field: "Water_Inj_IIC",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: "Water Injection - IPC",
                  field: "Water_Inj_IPC",
                  filtering: false,
                  hidden: true,
                  cellStyle: {
                    width: "5%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "5%",
                  },
                },
                {
                  title: t("editingReasonProdValuesTable"),
                  field: "editReason",
                  filtering: false,
                  editable: "always",
                  cellStyle: {
                    width: "15%",
                  },
                  headerStyle: {
                    fontWeight: "bold",
                    width: "15%",
                  },
                  render: (rowData) => {
                    const matchedEditReason = cpoEditReasons?.find(
                      (editReason) =>
                        rowData.editReason === editReason.reasonNumber,
                    );
                    if (matchedEditReason) {
                      return matchedEditReason.reason;
                    } else {
                      return "N/A";
                    }
                  },
                  lookup: cpoEditReasons?.reduce((acc, editReason) => {
                    acc[editReason.reasonNumber] = editReason.reason;
                    return acc;
                  }, {}),
                },
              ]}
            />
          </Grid>
        </Paper>
      </>
  );
};
